import React from 'react';
import { motion } from 'framer-motion';

/* ─────────────────────────────────────────────────────────────
   0. ORBITAL BADGE
   Spinning circular SVG text around a monogram — section break
──────────────────────────────────────────────────────────────── */
export function OrbitalBadge() {
  return (
    <div className="relative w-full flex items-center justify-center gap-0 py-6 overflow-hidden">
      {/* Left fading rule */}
      <div
        className="flex-1 h-px"
        style={{ background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.12))' }}
      />

      {/* Spinning badge */}
      <div className="relative w-24 h-24 shrink-0 mx-6">
        {/* Outer rotating ring with circular text */}
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 w-full h-full"
          style={{ animation: 'spin 18s linear infinite' }}
        >
          <defs>
            <path
              id="orb-path"
              d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0"
            />
          </defs>
          <text
            fill="rgba(124,58,237,0.5)"
            fontFamily="monospace"
            fontSize="7.2"
            fontWeight="800"
            letterSpacing="2.8"
          >
            <textPath href="#orb-path">
              FULL STACK · OPEN SOURCE · AI BUILDER ·
            </textPath>
          </text>
        </svg>

        {/* Static center monogram */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-purple-600 border-[3px] border-black flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
            <span className="text-white font-black text-base tracking-tight">KS</span>
          </div>
        </div>

        {/* Orbit dot */}
        <div
          className="absolute w-2.5 h-2.5 rounded-full bg-black border-2 border-white"
          style={{
            top: '50%',
            left: '50%',
            transformOrigin: '-34px 0',
            transform: 'translateY(-50%)',
            animation: 'spin 18s linear infinite',
          }}
        />
      </div>

      {/* Right fading rule */}
      <div
        className="flex-1 h-px"
        style={{ background: 'linear-gradient(to left, transparent, rgba(0,0,0,0.12))' }}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   1. MARQUEE TICKER
   Infinite scrolling strip of keywords — between About & Stack
──────────────────────────────────────────────────────────────── */
const TICKER_ITEMS = [
  'React', '·', 'NestJS', '·', 'FastAPI', '·', 'PostgreSQL',
  '·', 'TypeScript', '·', 'Docker', '·', 'MongoDB', '·',
  'WebSockets', '·', 'LangChain', '·', 'Stellar', '·',
  'Open Source', '·', 'AI Agents', '·', 'RAG Pipelines', '·',
];

export function MarqueeTicker() {
  const repeated = [...TICKER_ITEMS, ...TICKER_ITEMS, ...TICKER_ITEMS];

  return (
    <div className="relative w-full overflow-hidden border-y border-black/8 bg-black py-2.5 my-0">
      <div
        className="flex gap-5 whitespace-nowrap"
        style={{
          animation: 'marquee 22s linear infinite',
          width: 'max-content',
        }}
      >
        {repeated.map((item, i) => (
          <span
            key={i}
            className={`text-xs font-mono font-bold tracking-widest uppercase select-none ${item === '·' ? 'text-purple-500' : 'text-white/70'
              }`}
          >
            {item}
          </span>
        ))}
      </div>

      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   2. STAT BAR
   Bold numbers in a pale strip — between Experience & OpenSource
──────────────────────────────────────────────────────────────── */
const STATS = [
  { n: '2+', label: 'Years coding' },
  { n: '10+', label: 'Projects shipped' },
  { n: '5+', label: 'PRs merged' },
  { n: '$1k+', label: 'OSS bounties' },
  { n: '2', label: 'Internships' },
];

export function StatBar() {
  return (
    <div className="relative w-full border-y border-black/8 overflow-hidden">
      {/* Subtle purple tint */}
      <div className="absolute inset-0 bg-purple-600/[0.04] pointer-events-none" />

      <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-14 py-5">
        <div className="flex items-center gap-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {STATS.map(({ n, label }, i) => (
            <React.Fragment key={label}>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.35 }}
                className="flex-1 text-center min-w-[80px]"
              >
                <span className="block text-xl sm:text-2xl font-black text-black leading-none">{n}</span>
                <span className="block text-[9px] font-mono text-black/35 uppercase tracking-widest mt-1">{label}</span>
              </motion.div>
              {i < STATS.length - 1 && (
                <div className="w-px h-8 bg-black/8 shrink-0" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   3. TERMINAL BLOCK
   Fake terminal prompt — between OpenSource & Projects
──────────────────────────────────────────────────────────────── */
const LINES = [
  { prompt: '$', cmd: 'git clone https://github.com/Krishna41357', delay: 0 },
  { prompt: '$', cmd: 'cd portfolio && npm run dev', delay: 0.3 },
  { prompt: '#', cmd: 'Building in public since 2022 · 10+ shipped projects', delay: 0.7, comment: true },
  { prompt: '$', cmd: 'cat status.txt', delay: 1.1 },
  { prompt: '>', cmd: 'open_to_work: true | available_for_hire: true', delay: 1.4, output: true },
];

export function TerminalBlock() {
  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-14 py-4">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4 }}
        className="bg-[#0f0f0f] rounded-2xl border border-white/8 overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.25)]"
      >
        {/* Chrome */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/6">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
          <span className="ml-auto text-white/20 font-mono text-[9px] tracking-wider">bash — krishna</span>
        </div>

        {/* Lines */}
        <div className="px-5 py-4 space-y-1.5">
          {LINES.map(({ prompt, cmd, delay, comment, output }, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay, duration: 0.3 }}
              className="flex items-start gap-2.5 font-mono text-[11px] leading-relaxed"
            >
              <span className={`shrink-0 font-bold ${output ? 'text-purple-400' :
                  comment ? 'text-white/20' :
                    'text-green-400'
                }`}>
                {prompt}
              </span>
              <span className={
                output ? 'text-purple-300' :
                  comment ? 'text-white/25 italic' :
                    'text-white/80'
              }>
                {cmd}
                {i === LINES.length - 1 && (
                  <span className="inline-block w-1.5 h-3.5 bg-white/70 ml-1 animate-pulse align-text-bottom" />
                )}
              </span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   4. QUOTE BAND
   Full-width bold pull-quote — between Projects & DsaStatus
──────────────────────────────────────────────────────────────── */
export function QuoteBand() {
  return (
    <div className="relative w-full border-y border-black/8 overflow-hidden">
      {/* Purple gradient fill */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 pointer-events-none" />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 lg:px-14 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 mb-2">Philosophy</p>
          <p className="text-xl sm:text-2xl font-black text-white leading-snug tracking-tight">
            "Ship fast, iterate often,<br className="hidden sm:block" />
            <span className="text-purple-200"> and never stop learning."</span>
          </p>
          <p className="text-[11px] font-mono text-white/40 mt-3">— Krishna Srivastava, Full Stack Developer</p>
        </motion.div>
      </div>
    </div>
  );
}
